/**
 * Mint new codes in each customer-selectable format.
 *
 * Every format is deterministic given a clock and a random source, so tests can pin both. Within one
 * minter instance a code is never returned twice: a collision advances the format's own clock unit
 * (Classic: 1 ms, SiteManager: 1 s) or redraws the random part (Standard), so legacy shapes are kept.
 */

const CALENDAR_MAP = [ '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V' ];

/** Crockford base32: no I, L, O, U, so codes read aloud and typed by hand survive. */
const CROCKFORD_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

const MAX_ATTEMPTS = 1000;

/**
 * @typedef {'Standard'|'Classic'|'SiteManager'} CodeFormat
 */

/**
 * @typedef {Object} MintContext
 * @property {number} [IDProject]
 * @property {number} [IDUser]
 * @property {string} [ExternalSyncGUID] - SiteManager prefix source (the user's CMS id).
 * @property {string} [TimeZone] - IANA zone for date parts; runtime local when omitted.
 */

/**
 * @typedef {Object} SiteManagerOptions
 * @property {number} [MaxPrefixLength] - Prefixes longer than this are cut. Default 9, as the materials app cuts them.
 * @property {number} [TruncateTo] - Length a too-long prefix is cut to. Default MaxPrefixLength.
 * @property {'12'|'24'} [HourFormat] - Default '12' (01-12), as the materials app formats it.
 */

/**
 * @param {number} pRandomBits
 * @return {number}
 */
function defaultRandomInt(pRandomBits)
{
	const tmpCrypto = (typeof globalThis !== 'undefined' && globalThis.crypto && typeof globalThis.crypto.getRandomValues === 'function') ? globalThis.crypto : null;
	if (tmpCrypto)
	{
		const tmpBuffer = new Uint32Array(1);
		tmpCrypto.getRandomValues(tmpBuffer);
		return tmpBuffer[0] % pRandomBits;
	}
	return Math.floor(Math.random() * pRandomBits);
}

/**
 * Date parts in a time zone (or the runtime's zone), using Intl so no date library is needed.
 *
 * @param {Date} pDate
 * @param {string} [pTimeZone]
 * @return {{ Year: number, Month: number, Day: number, Hour: number, Minute: number, Second: number }}
 */
function getDateParts(pDate, pTimeZone)
{
	const tmpOptions =
	{
		timeZone: pTimeZone || undefined,
		year: 'numeric', month: 'numeric', day: 'numeric',
		hour: 'numeric', minute: 'numeric', second: 'numeric',
		hourCycle: 'h23',
	};
	let tmpFormatter;
	try
	{
		tmpFormatter = new Intl.DateTimeFormat('en-US', tmpOptions);
	}
	catch (pError)
	{
		// A zone Intl doesn't know (a stored user setting) falls back to the runtime's zone rather than failing the mint.
		tmpFormatter = new Intl.DateTimeFormat('en-US', Object.assign(tmpOptions, { timeZone: undefined }));
	}
	const tmpParts = {};
	for (const tmpPart of tmpFormatter.formatToParts(pDate))
	{
		tmpParts[tmpPart.type] = tmpPart.value;
	}
	return {
		Year: Number(tmpParts.year),
		Month: Number(tmpParts.month),
		Day: Number(tmpParts.day),
		Hour: Number(tmpParts.hour) % 24,
		Minute: Number(tmpParts.minute),
		Second: Number(tmpParts.second),
	};
}

/**
 * @param {number|string} pValue
 * @param {number} pLength
 * @return {string}
 */
function leftPad(pValue, pLength)
{
	return String(pValue).padStart(pLength, '0');
}

/**
 * @param {any} pID
 * @return {string} upper-case base36 of a positive integer id, or '0'
 */
function idToBase36(pID)
{
	const tmpNumber = parseInt(pID, 10);
	return (tmpNumber > 0) ? tmpNumber.toString(36).toUpperCase() : '0';
}

class QRCodeMinter
{
	/**
	 * @param {Object} [pOptions]
	 * @param {() => Date} [pOptions.Clock]
	 * @param {(pMax: number) => number} [pOptions.RandomInt] - Returns an integer in [0, pMax).
	 * @param {(pCode: string) => Promise<boolean>} [pOptions.IsTaken] - Online uniqueness check, used by mintAsync.
	 */
	constructor(pOptions)
	{
		const tmpOptions = pOptions || {};
		this.clock = tmpOptions.Clock || (() => new Date());
		this.randomInt = tmpOptions.RandomInt || defaultRandomInt;
		this.isTaken = tmpOptions.IsTaken || null;
		/** @type {Set<string>} */
		this.minted = new Set();
	}

	/**
	 * Mint a code that this minter has not returned before.
	 *
	 * @param {CodeFormat} pFormat
	 * @param {MintContext} [pContext]
	 * @param {Object} [pFormatOptions] - SiteManagerOptions for SiteManager.
	 * @return {string}
	 */
	mint(pFormat, pContext, pFormatOptions)
	{
		const tmpContext = pContext || {};
		let tmpDate = this.clock();
		for (let i = 0; i < MAX_ATTEMPTS; i++)
		{
			const tmpCode = this.build(pFormat, tmpContext, pFormatOptions, tmpDate);
			if (!this.minted.has(tmpCode))
			{
				this.minted.add(tmpCode);
				return tmpCode;
			}
			tmpDate = this.advance(pFormat, tmpDate);
		}
		throw new Error(`Could not mint a unique ${pFormat} code after ${MAX_ATTEMPTS} attempts.`);
	}

	/**
	 * Mint a code that is also free on the server (when an IsTaken check is configured).
	 *
	 * @param {CodeFormat} pFormat
	 * @param {MintContext} [pContext]
	 * @param {Object} [pFormatOptions]
	 * @return {Promise<string>}
	 */
	async mintAsync(pFormat, pContext, pFormatOptions)
	{
		for (let i = 0; i < 20; i++)
		{
			const tmpCode = this.mint(pFormat, pContext, pFormatOptions);
			if (!this.isTaken || !(await this.isTaken(tmpCode)))
			{
				return tmpCode;
			}
		}
		throw new Error(`Could not mint a ${pFormat} code that is free on the server.`);
	}

	/**
	 * Build the code for a given instant, without the uniqueness loop.
	 *
	 * @param {CodeFormat} pFormat
	 * @param {MintContext} pContext
	 * @param {Object} [pFormatOptions]
	 * @param {Date} pDate
	 * @return {string}
	 */
	build(pFormat, pContext, pFormatOptions, pDate)
	{
		switch (pFormat)
		{
			case 'Standard':
				return this.buildStandard(pContext, pDate);
			case 'Classic':
				return this.buildClassic(pContext, pDate);
			case 'SiteManager':
				return this.buildSiteManager(pContext, pFormatOptions || {}, pDate);
			default:
				throw new Error(`Unknown code format "${pFormat}".`);
		}
	}

	/**
	 * @param {CodeFormat} pFormat
	 * @param {Date} pDate
	 * @return {Date} the next instant to try after a collision
	 */
	advance(pFormat, pDate)
	{
		switch (pFormat)
		{
			case 'Classic':
				return new Date(pDate.getTime() + 1);
			case 'SiteManager':
				return new Date(pDate.getTime() + 1000);
			default:
				return pDate;
		}
	}

	/**
	 * `<IDProject b36>-<IDUser b36>-<YY><M><D><6 Crockford base32>`, e.g. `QKI-FF1-268K7G3QXM`.
	 * Uppercase only, so it is safe under case-insensitive collation and fits QR alphanumeric mode.
	 *
	 * @param {MintContext} pContext
	 * @param {Date} pDate
	 * @return {string}
	 */
	buildStandard(pContext, pDate)
	{
		const tmpParts = getDateParts(pDate, pContext.TimeZone);
		let tmpRandom = '';
		for (let i = 0; i < 6; i++)
		{
			tmpRandom += CROCKFORD_ALPHABET[this.randomInt(32)];
		}
		return `${idToBase36(pContext.IDProject)}-${idToBase36(pContext.IDUser)}-${leftPad(tmpParts.Year % 100, 2)}${CALENDAR_MAP[tmpParts.Month]}${CALENDAR_MAP[tmpParts.Day]}${tmpRandom}`;
	}

	/**
	 * The pre-2026 web format, unchanged: `<IDProject b36>-<IDUser b36>-<YY><M><D><hh%12><mm><ss><ms b36, 2>`.
	 * Runtime-local clock, as the original generator used.
	 *
	 * @param {MintContext} pContext
	 * @param {Date} pDate
	 * @return {string}
	 */
	buildClassic(pContext, pDate)
	{
		const tmpParts = getDateParts(pDate, pContext.TimeZone);
		const tmpMilliseconds = leftPad(pDate.getMilliseconds().toString(36), 2);
		return `${idToBase36(pContext.IDProject)}-${idToBase36(pContext.IDUser)}-${leftPad(tmpParts.Year % 100, 2)}${CALENDAR_MAP[tmpParts.Month]}${CALENDAR_MAP[tmpParts.Day]}${leftPad(tmpParts.Hour % 12, 2)}${leftPad(tmpParts.Minute, 2)}${leftPad(tmpParts.Second, 2)}${tmpMilliseconds}`;
	}

	/**
	 * The SiteManager / LADOTD format: `<prefix><YY><M><D><hh><mm><ss>`, prefix = the user's CMS id, else `HL<IDUser>`.
	 * The defaults follow the materials app (`materials-sampletracking` `sample.ts` `generateGUID`), and so do the API
	 * and the report_definitions forms mixin (`global/js/mixins/sample.mixins.js` `GenerateSampleQRCode`).
	 *
	 * @param {MintContext} pContext
	 * @param {SiteManagerOptions} pOptions
	 * @param {Date} pDate
	 * @return {string}
	 */
	buildSiteManager(pContext, pOptions, pDate)
	{
		const tmpMaxPrefix = pOptions.MaxPrefixLength || 9;
		let tmpPrefix = pContext.ExternalSyncGUID ? String(pContext.ExternalSyncGUID) : `HL${parseInt(pContext.IDUser, 10) || 0}`;
		if (tmpPrefix.length > tmpMaxPrefix)
		{
			tmpPrefix = tmpPrefix.substring(0, pOptions.TruncateTo || tmpMaxPrefix);
		}
		const tmpParts = getDateParts(pDate, pContext.TimeZone);
		let tmpHour = tmpParts.Hour;
		if ((pOptions.HourFormat || '12') === '12')
		{
			tmpHour = (tmpHour % 12) || 12;
		}
		return `${tmpPrefix}${leftPad(tmpParts.Year % 100, 2)}${CALENDAR_MAP[tmpParts.Month]}${CALENDAR_MAP[tmpParts.Day]}${leftPad(tmpHour, 2)}${leftPad(tmpParts.Minute, 2)}${leftPad(tmpParts.Second, 2)}`;
	}
}

module.exports = QRCodeMinter;
module.exports.CROCKFORD_ALPHABET = CROCKFORD_ALPHABET;
module.exports.getDateParts = getDateParts;
